import {ComponentProps, useState} from "react";
import PageHeaderLayout from "../../../../layout/pageHeaderLayout";
import {Button, Card, InputNumber, message, Popconfirm, Space} from "antd";
import useAntTable from "../../../../utils/useAntTable";
import * as api from "../../../../apis/platforms/dealer/cart";
import StandardTable, {StandardColumnsType} from "../../../../components/StandardTable";
import Upload from "../../../../components/Upload";
import Divider from "../../../../components/Divider";
import {useRequest} from "ahooks";
import styles from "./index.module.css";
import OrderResultListModal from "./components/OrderResultListModal";
import {useSelector} from "react-redux";
import {RootState} from "../../../../models";
import withAuth from "../../../../utils/withAuth";
import {Decimal} from "decimal.js";
import useAuth from "../../../../utils/useAuth";

const SalesOrderButton = withAuth('salesOrder', Button);
const ConsignOrderButton = withAuth('consignOrder', Button);
const ExchangeOrderButton = withAuth('exchangeOrder', Button);


function Cart(props: ComponentProps<any>) {
    const showPrice = useAuth().indexOf('showPrice') > -1;
    const [products, setProducts] = useState([]);
    const [productsQty, setProductsQty] = useState<any>({});
    const [orderResultList, setOrderResultList] = useState<Array<any>>([]);
    const [resultModalVisible, setResultModalVisible] = useState(false);
    const [enableConsignment, setEnableConsignment] = useState(false);
    const {tableProps, form, refresh} = useAntTable(api.getCart, {
        selectTable: true,
        onSuccess(data) {
            const qtyDict: any = {};
            (data.list || []).forEach((p: any) => qtyDict[p.id] = p.qty);
            setProductsQty({...qtyDict});
            setProducts(data.list || []);
            const consignmentFlag = (data.list || []).map((p: any) => p?.enableConsignment).reduce((f1 = false, f2 = false) => f1 || f2);
            setEnableConsignment(consignmentFlag);
        }
    });
    tableProps.rowSelection.onSelect = (record: any, selected: boolean, selectedRows: Array<any>, event: any) => {
        if (!record.suitId) {
            return;
        }
        if (selected) {
            const newSelected = products.filter((p: any) => p.suitId == record.suitId);
            const oldSelected = selectedRows.filter((p: any) => p.id !== record.id);
            tableProps.rowSelection.onChange([...oldSelected, ...newSelected].map(p => p.id), [...oldSelected, ...newSelected]);
            // tableProps.rowSelection.onChange([],[])
        } else {
            const newSelected = selectedRows.filter(p => p.suitId !== record.suitId);
            tableProps.rowSelection.onChange(newSelected.map(p => p.id), newSelected);
            // tableProps.rowSelection.onChange([],[])
        }
        event.defaultPrevented = true;
    }

    const {loading: loadingUpdateCart, run: updateCart} = useRequest(api.modifyCartProduct, {
        manual: true,
        onSuccess(res) {
            refresh();
        }
    });

    const {loading: loadingDelete, run: deleteCart} = useRequest(api.deleteCartProduct, {
        manual: true,
        onSuccess(res) {
            refresh();
        }
    });

    const {loading: loadingSubmitOrder, run: runSubmitOrder} = useRequest(api.submitOrder, {
        manual: true,
        onSuccess(res) {
            refresh();
            const results: Array<any> = res.data.data;
            setOrderResultList(results);
            setResultModalVisible(true);
        }
    });
    const {loading: loadingSubmitExchangeOrder, run: runSubmitExchangeOrder} = useRequest(api.submitExchangeOrder, {
        manual: true,
        onSuccess(res) {
            refresh();
            const results: Array<any> = res.data.data;
            setOrderResultList(results);
            setResultModalVisible(true);
        }
    })
    const {loading: loadingSubmitConsign, run: runSubmitConsign} = useRequest(api.submitConsign, {
        manual: true,
        onSuccess(res) {
            refresh();
            const result: Array<any> = res?.data?.data;
            setOrderResultList(result);
            setResultModalVisible(true);
        }
    });

    const checkHasNoneQty = () => {
        const errProducts = tableProps.rowSelection.selectedRows.filter((p: any) => !p.qty) || [];
        errProducts.forEach((p: any) => message.error(`请填写${p.productName}下单数量`));
        return !!errProducts.length;
    }

    const getSummary = () => {
        const amounts = products.map((p: any) => new Decimal(p.amount)) || [];
        return amounts.reduce((i: Decimal = new Decimal(0), j: Decimal = new Decimal(0)) => Decimal.add(i, j), new Decimal(0))?.toString();
    }

    const handleChange = (v: number, record: any) => {
        const qtyDict = productsQty;
        if (!!record.suitId) {
            products.filter((p: any) => p.suitId == record.suitId).forEach((rec: any) => qtyDict[rec.id] = v * rec.suitQty);
            // qtyDict[record.id] = v * record.suitQty;
        } else {
            qtyDict[record.id] = v;
        }
        setProductsQty({...qtyDict});
    }

    const columns: StandardColumnsType = [
        {
            dataIndex: 'image',
            key: 'image',
            title: '图片',
            render(_, record: any) {
                return (!!record.suitId && !record.productId) ? null :
                    <Upload value={record.image} disabled listType={'picture-card'} maxCount={1}>
                        <br/>
                    </Upload>
            }
        },
        {
            dataIndex: 'suitName',
            key: 'suitName',
            title: '套餐名称',
            width: 100
        },
        {
            dataIndex: 'productCode',
            key: 'productCode',
            title: '商品编号',
            width: 100
        },
        {
            dataIndex: 'productName',
            key: 'productName',
            title: '商品名称',
            width: 100
        },
        {
            dataIndex: 'hospitalFullName',
            key: 'hospitalFullName',
            title: '终端医院',
            width: 120
        },
        {
            dataIndex: 'specification',
            key: 'specification',
            title: '规格型号',
            width: 100
        },
        {
            dataIndex: 'abbreviation',
            key: 'abbreviation',
            title: '英文缩写',
            width: 80
        },
        {
            dataIndex: 'unit',
            key: 'unit',
            title: '单位',
            width: 60
        },
        {
            dataIndex: 'method',
            key: 'method',
            title: '方法',
            width: 100
        },
        {
            dataIndex: 'useIn',
            key: 'useIn',
            title: '适用机型',
            width: 100
        },
        ...showPrice ? [
            {
                dataIndex: 'price',
                key: 'price',
                title: '销售价格',
                width: 100
            },
            {
                dataIndex: 'amount',
                key: 'amount',
                title: '总价',
                width: 100
            },
        ] : [],

        {
            dataIndex: 'restrictQty',
            key: 'restrictQty',
            title: '限购数量',
            width: 100,
        },
        {
            dataIndex: 'remainQty',
            key: 'remainQty',
            title: '限购剩余数量',
            width: 100,
            render(v: any) {
                return Number.isInteger(v) && v < 0 ? 0 : v
            }
        },
        {
            dataIndex: 'qty',
            key: 'qty',
            fixed: 'right',
            width: 140,
            title: '数量',
            render(_, record: any) {
                if (record.suitId) {
                    return <InputNumber step={1} value={(productsQty[record.id] / record.suitQty) || 0} min={1}
                                        onChange={v => handleChange(v || 0, record)} style={{width: '100%'}}
                                        addonBefore={`${record.suitQty} x `} onBlur={(e) => submitQtyChange(record)}
                                        onKeyPress={e => e.which == 13 && submitQtyChange(record)}/>
                }
                return <InputNumber step={1} value={productsQty[record.id]} onChange={v => handleChange(v || 0, record)}
                                    min={1}
                                    onBlur={(e) => submitQtyChange(record)} style={{width: '100%'}}
                                    onKeyPress={e => e.which == 13 && submitQtyChange(record)}/>
            }
        },
        {
            dataIndex: 'operation',
            key: 'operation',
            fixed: "right",
            width: 100,
            title: '操作',
            render(_, record: any) {
                return <Divider>
                    <Popconfirm title={record.suitId ? '删除套餐将移除套餐下全部商品，是否确认删除？' : '是否确认删除？'}
                                onConfirm={() => handleDelete([record.id])}>
                        <a style={{color: 'var(--danger-color)'}}>删除</a>
                    </Popconfirm>
                </Divider>
            }
        }
    ];

    function handleDelete(ids: Array<number>) {
        deleteCart(ids);
    }

    function handleMultipleDelete() {
        const ids = tableProps.rowSelection?.selectedRowKeys || [];
        deleteCart(ids);
    }

    function submitSales() {
        if (checkHasNoneQty()) {
            return;
        }
        const ids = tableProps.rowSelection?.selectedRowKeys || [];
        if (!ids.length) {
            message.error('请勾选需要下单的商品。')
            return;
        }
        runSubmitOrder(ids);
    }

    function submitExchangeOrder() {
        if (checkHasNoneQty()) {
            return;
        }
        const ids = tableProps.rowSelection?.selectedRowKeys || [];
        if (!ids.length) {
            message.error('请勾选需要下单的商品。')
            return;
        }
        runSubmitExchangeOrder(ids);

    }

    function submitConsign() {
        if (checkHasNoneQty()) {
            return;
        }
        const ids = tableProps.rowSelection?.selectedRowKeys || [];
        if (!ids.length) {
            message.error('请勾选需要下单的商品。')
            return;
        }
        const records = tableProps.rowSelection?.selectedRows || [];
        const product = records.find((p: any) => !p.enableConsignment);
        if (!!product) {
            message.error(product.suitName ? `套装（${product.suitName}）不可寄售` : `商品（${product.productName}）不可寄售`);
            return;
        }
        runSubmitConsign(ids);


    }

    function submitQtyChange(record: any) {
        const qty = productsQty[record.id];
        if (!record.suitId) {
            record.qty = qty;
            updateCart([record]);
        } else {
            const suitProducts = products.filter((p: any) => p?.suitId == record.suitId).map((p: any) => {
                return {...p, qty: productsQty[p.id]}
            });
            updateCart(suitProducts);
        }
    }

    return (
        <div style={{maxHeight: 'calc(100vh - 48px)', position: "relative", overflowY: "scroll"}}>
            <PageHeaderLayout>
                <StandardTable {...tableProps} form={form} submit={form.submit} columns={columns} rowKey={'id'}
                               pagination={false} scroll={{x: 'max-content'}}/>
                <br/>
                <br/>
            </PageHeaderLayout>
            <Card className={styles.FootBar}>
                <Space>
                    <a onClick={handleMultipleDelete}>清空选中项</a>
                    <span className={styles.Summary}>合计：{getSummary()}</span>
                </Space>
                &nbsp;&nbsp;&nbsp;
                <Space className={styles.Buttons}>
                    <SalesOrderButton key={'submit'} type={"primary"} onClick={submitSales} loading={loadingSubmitOrder|| tableProps.loading}>立即下单</SalesOrderButton>
                    {enableConsignment &&
                        <ConsignOrderButton key={'apply'} type={"primary"}
                                            onClick={submitConsign}>寄售申请</ConsignOrderButton>}
                    <ExchangeOrderButton key={'exchange'} onClick={submitExchangeOrder} loading={loadingSubmitExchangeOrder|| tableProps.loading}>换货订单</ExchangeOrderButton>
                </Space>
            </Card>
            <OrderResultListModal open={resultModalVisible} records={orderResultList}
                                  onCancel={() => setResultModalVisible(false)}/>
        </div>
    )
}

export default Cart;
